import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQrScanner } from '../hooks/useQrScanner';
import {
  identify,
  punch,
  heartbeat,
  PUNCH_LABELS,
  PunchAction,
  KioskIdentifyResult,
  PunchTimes,
} from '../services/kioskService';

/**
 * 共用端末(キオスク)の打刻画面。
 *
 * ## 画面の流れ
 *
 *   待機(QRをかざしてください) → 読み取り → 動作を選ぶ → 完了表示 → 3秒で待機へ
 *
 * ## 見た目はダッシュボードの打刻欄に揃える
 *
 * **同じ「打刻」という行為が、端末によって別物に見えないようにする。**
 * 白地・青枠の現在時刻・緑=出勤/赤=退勤/青=休憩という配色は、本人がスマホで
 * 打刻するときの画面と同じもの。従業員は両方を使い分けるので、
 * ここだけ独自の見た目にすると覚え直しが要る。
 * ただし文字とボタンは**大きく**する(立ったまま、離れた位置から操作するため)。
 *
 * ## この画面が守っていること
 *
 * - **ログインしない。** 認証は端末トークン Cookie(層1)。`useAuth` を使わない
 * - **他人の情報を出さない。** 名簿を出さず、読み取った本人の氏名だけを表示する。
 *   店頭に置かれ来客の目に入るため(脅威 T3)
 * - **押せる動作だけを出す。** 出勤中の人に［出勤］を見せない。現場での誤操作を減らす
 * - **完了後は自動で待機へ戻る。** 前の人の名前が residual に残らないようにする(脅威 T6)
 */

type Phase =
  | { kind: 'waiting' }
  | { kind: 'identified'; result: KioskIdentifyResult; qrToken: string }
  | {
      kind: 'done';
      name: string;
      action: PunchAction;
      time: string;
      times: PunchTimes;
      autoBreakDeduction: boolean;
    }
  | { kind: 'error'; message: string };

const AUTO_RETURN_MS = 3000;

/** ダッシュボードの打刻ボタンと同じ配色 */
const ACTION_COLORS: Record<PunchAction, { bg: string; hover: string }> = {
  clock_in:    { bg: '#2ea44f', hover: '#2c974b' },
  clock_out:   { bg: '#cf222e', hover: '#a40e26' },
  break_start: { bg: '#0969da', hover: '#0860ca' },
  break_end:   { bg: '#0969da', hover: '#0860ca' },
};

const KioskPage: React.FC = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>({ kind: 'waiting' });
  const [deviceName, setDeviceName] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const timerRef = useRef<number | null>(null);

  // 読み取りを止めるのは「動作を選んでいる間」と「完了表示中」。
  // 待機中だけカメラを回す
  const scanning = phase.kind === 'waiting';

  const backToWaiting = useCallback(() => {
    setPhase({ kind: 'waiting' });
  }, []);

  const handleDetect = useCallback(async (value: string) => {
    setBusy(true);
    try {
      const result = await identify(value);
      setPhase({ kind: 'identified', result, qrToken: value });
    } catch (e: any) {
      setPhase({
        kind: 'error',
        message: e.response?.data?.error || 'QRコードを読み取れませんでした',
      });
    } finally {
      setBusy(false);
    }
  }, []);

  const {
    videoRef, canvasRef, status, failure, engine, resetLastValue,
    cameras, cameraId, switchCamera,
  } = useQrScanner({ onDetect: handleDetect, active: scanning });

  // 端末が登録済みかを最初に確かめる。未登録ならペアリング画面へ送る
  useEffect(() => {
    heartbeat()
      .then((r) => setDeviceName(r.deviceName))
      .catch(() => navigate('/kiosk/setup', { replace: true }));
  }, [navigate]);

  // 完了・エラーの表示は数秒で待機へ戻す
  useEffect(() => {
    if (phase.kind === 'done' || phase.kind === 'error') {
      timerRef.current = window.setTimeout(() => {
        resetLastValue(); // 同じ人が続けて打てるように直近値を忘れる
        backToWaiting();
      }, AUTO_RETURN_MS);
    }
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [phase, backToWaiting, resetLastValue]);

  const doPunch = async (action: PunchAction) => {
    if (phase.kind !== 'identified' || busy) return;
    setBusy(true);
    try {
      const r = await punch(phase.qrToken, action);
      setPhase({
        kind: 'done',
        name: r.user.name,
        action: r.action,
        time: r.time,
        times: r.times,
        autoBreakDeduction: r.autoBreakDeduction,
      });
    } catch (e: any) {
      setPhase({ kind: 'error', message: e.response?.data?.error || '打刻に失敗しました' });
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    resetLastValue();
    backToWaiting();
  };

  const timeLabel = (iso: string) =>
    new Date(iso).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });

  return (
    <div style={s.page}>
      {/* ヘッダー。ダッシュボードと同じ白カード + 罫線 */}
      <header style={s.header}>
        <div style={s.brandBox}>
          {/*
            アイコンマーク（文字を含まない方）を使う。
            すぐ隣に「キンログ」と書くので、名前入りのロックアップだと重複する。
          */}
          <img src="/favicon.svg" alt="" aria-hidden style={s.logo} />
          <div>
            <div style={s.brand}>キンログ</div>
            <div style={s.deviceName}>打刻端末 · {deviceName || '共用端末'}</div>
          </div>
        </div>
        {engine && <span style={s.engine}>読み取り: {engine === 'native' ? '内蔵' : '互換'}</span>}
      </header>

      <main style={s.card}>
        <h1 style={s.h1}>出退勤の打刻</h1>

        {/* 現在時刻。ダッシュボードの「現在時刻」枠と同じ体裁で、字を大きく */}
        <Clock />

        {phase.kind === 'waiting' && (
          <>
            <div style={s.videoWrap}>
              <video ref={videoRef} style={s.video} muted playsInline />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              <div style={s.reticle} />
            </div>
            <p style={s.guide}>
              {busy
                ? '読み取り中です。そのままお待ちください'
                : 'ご自分のQRコードを、上の枠にかざしてください'}
            </p>
            <p style={s.guideSub}>
              スマートフォンの方は「共用端末用のQRコード」を表示してください
            </p>
            {/*
              カメラが複数ある端末では切り替えられるようにする。
              背面固定だと、内カメラしか無い端末や壁掛けで内カメラを使いたい
              設置で詰む。ノートPCで外付けと内蔵が両方ある場合も同じ。
            */}
            {cameras.length > 1 && (
              <button type="button" onClick={switchCamera} style={s.cameraBtn}>
                カメラを切り替える（{(cameras.findIndex((c) => c.deviceId === cameraId) + 1) || 1}/
                {cameras.length}）
              </button>
            )}

            {/*
              **原因ごとに案内を変える。** 「カメラを使用できません」で
              まとめると、実際には別の理由で止まっているときに
              打つ手が分からなくなる（古い iPad で実際に誤診した）。
            */}
            {failure?.kind === 'denied' && (
              <p style={s.warn}>
                カメラの使用が許可されていません。ブラウザの設定でこのサイトのカメラを許可し、
                画面を再読み込みしてください。
              </p>
            )}
            {failure?.kind === 'no_api' && (
              <p style={s.warn}>
                このブラウザはカメラでの読み取りに対応していません。
                iPhone・iPad では <strong>Safari</strong> でお試しください。
                それでも表示される場合は、端末の OS が古い可能性があります。
              </p>
            )}
            {failure?.kind === 'no_camera' && (
              <p style={s.warn}>カメラが見つかりません。この端末にカメラが搭載されているかご確認ください。</p>
            )}
            {failure?.kind === 'decoder_failed' && (
              <p style={s.warn}>
                QRコードの読み取り機能を読み込めませんでした。
                通信環境をご確認のうえ画面を再読み込みしてください。
                繰り返す場合は、この端末のブラウザが古い可能性があります。
              </p>
            )}
            {failure?.kind === 'camera_error' && (
              <p style={s.warn}>
                カメラを起動できませんでした（{failure.detail}）。
                他のアプリがカメラを使用していないかご確認のうえ、画面を再読み込みしてください。
              </p>
            )}
          </>
        )}

        {phase.kind === 'identified' && (
          <>
            <div style={s.person}>
              <div style={s.personNo}>
                {phase.result.user.employeeNumber
                  ? `社員番号 ${phase.result.user.employeeNumber}`
                  : ' '}
              </div>
              <div style={s.personName}>{phase.result.user.name} さん</div>
            </div>

            <TimeStrip
              times={phase.result.times}
              autoBreakDeduction={phase.result.autoBreakDeduction}
            />

            <div style={s.actions}>
              {phase.result.available.map((a) => (
                <button
                  key={a}
                  onClick={() => doPunch(a)}
                  disabled={busy}
                  style={{ ...s.actionBtn, backgroundColor: ACTION_COLORS[a].bg }}
                  onMouseOver={(e) =>
                    !busy && (e.currentTarget.style.backgroundColor = ACTION_COLORS[a].hover)
                  }
                  onMouseOut={(e) => (e.currentTarget.style.backgroundColor = ACTION_COLORS[a].bg)}
                >
                  {busy ? '処理中...' : PUNCH_LABELS[a]}
                </button>
              ))}
            </div>

            <button onClick={cancel} style={s.cancelBtn}>
              やめる
            </button>
          </>
        )}

        {phase.kind === 'done' && (
          <div style={s.result}>
            <div style={{ ...s.mark, backgroundColor: '#2ea44f' }}>✓</div>
            <div style={s.personName}>{phase.name} さん</div>
            <div style={{ ...s.resultText, color: ACTION_COLORS[phase.action].bg }}>
              {PUNCH_LABELS[phase.action]} {timeLabel(phase.time)}
            </div>
            <TimeStrip times={phase.times} autoBreakDeduction={phase.autoBreakDeduction} />
            <p style={s.guideSub}>次の方はQRコードをかざしてください</p>
          </div>
        )}

        {phase.kind === 'error' && (
          <div style={s.result}>
            <div style={{ ...s.mark, backgroundColor: '#cf222e' }}>!</div>
            <p style={s.errorText}>{phase.message}</p>
          </div>
        )}
      </main>
    </div>
  );
};

/**
 * 打刻時刻の一覧。ダッシュボードの同じ帯（灰地・等分・ラベル+時刻）に揃える。
 *
 * 出すのは**打刻時刻だけ**。勤務時間の集計や残業の見込みは出さない——
 * 端末は店頭に置かれ来客の目に入るため（脅威 T3）。
 *
 * 休憩の自動控除が有効な会社では休憩の列を出さない。その運用では休憩打刻を
 * 使わないので、空欄が並ぶだけで意味が無い（ダッシュボードも同じ出し分け）。
 */
const TimeStrip: React.FC<{ times: PunchTimes; autoBreakDeduction: boolean }> = ({
  times,
  autoBreakDeduction,
}) => {
  const fmt = (iso: string | null) =>
    iso ? new Date(iso).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' }) : '---';

  const cells: Array<{ label: string; value: string; color: string }> = [
    { label: '出勤時刻', value: fmt(times.clockIn), color: '#2ea44f' },
    { label: '退勤時刻', value: fmt(times.clockOut), color: '#cf222e' },
  ];
  if (!autoBreakDeduction) {
    cells.push(
      { label: '休憩開始', value: fmt(times.breakStart), color: '#0969da' },
      { label: '休憩終了', value: fmt(times.breakEnd), color: '#0969da' }
    );
  }

  return (
    <div style={{ ...s.strip, gridTemplateColumns: `repeat(${cells.length}, 1fr)` }}>
      {cells.map((c) => (
        <div key={c.label}>
          <div style={s.stripLabel}>{c.label}</div>
          <div style={{ ...s.stripValue, color: c.value === '---' ? '#8b949e' : c.color }}>
            {c.value}
          </div>
        </div>
      ))}
      {autoBreakDeduction && (
        <div style={s.stripNote}>休憩時間は自動控除されます</div>
      )}
    </div>
  );
};

/** ダッシュボードの「現在時刻」枠に合わせる。打刻端末なので字を一段大きく */
const Clock: React.FC = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return (
    <div style={s.clockBox}>
      <div style={s.clockLabel}>現在時刻</div>
      <div style={s.clockTime}>
        {now.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </div>
      <div style={s.clockDate}>
        {now.toLocaleDateString('ja-JP', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'long',
        })}
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    padding: 'clamp(10px, 3vw, 20px)',
    // ダッシュボードと同じ淡い地色
    backgroundColor: '#f6f8fa',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  header: {
    maxWidth: 900,
    margin: '0 auto 16px',
    backgroundColor: 'white',
    border: '1px solid #d0d7de',
    borderRadius: 8,
    padding: 'clamp(12px, 2.5vw, 18px) clamp(14px, 3vw, 24px)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  brandBox: { display: 'flex', alignItems: 'center', gap: 'clamp(8px, 2vw, 12px)' },
  logo: {
    width: 'clamp(30px, 6vw, 40px)',
    height: 'clamp(30px, 6vw, 40px)',
    borderRadius: 8,
    display: 'block',
  },
  brand: { fontSize: 'clamp(16px, 3.5vw, 20px)', fontWeight: 700, color: '#24292e' },
  deviceName: { fontSize: 'clamp(11px, 2.2vw, 13px)', color: '#6c757d', marginTop: 2 },
  engine: { fontSize: 11, color: '#8b949e', whiteSpace: 'nowrap' },

  card: {
    maxWidth: 900,
    margin: '0 auto',
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 'clamp(18px, 4vw, 30px)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    textAlign: 'center',
  },
  h1: {
    marginTop: 0,
    marginBottom: 20,
    fontSize: 'clamp(17px, 3.6vw, 20px)',
    color: '#24292e',
    fontWeight: 600,
  },

  clockBox: {
    padding: 'clamp(18px, 4vw, 28px) 20px',
    borderRadius: 8,
    border: '2px solid #007bff',
    marginBottom: 'clamp(18px, 4vw, 28px)',
  },
  clockLabel: { fontSize: 13, color: '#6c757d', marginBottom: 10, fontWeight: 500 },
  clockTime: {
    // ダッシュボードは 56px 固定。立って使う端末なので上限を上げる
    fontSize: 'clamp(44px, 11vw, 76px)',
    fontWeight: 300,
    color: '#007bff',
    letterSpacing: 2,
    lineHeight: 1.1,
  },
  clockDate: { fontSize: 'clamp(12px, 2.6vw, 15px)', color: '#6c757d', marginTop: 10 },

  videoWrap: {
    position: 'relative',
    width: 'min(64vw, 300px)',
    aspectRatio: '1 / 1',
    margin: '0 auto',
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#f6f8fa',
    border: '1px solid #d0d7de',
  },
  video: { width: '100%', height: '100%', objectFit: 'cover' },
  reticle: {
    position: 'absolute',
    inset: '12%',
    border: '3px solid rgba(255,255,255,0.85)',
    borderRadius: 8,
    pointerEvents: 'none',
  },

  guide: {
    fontSize: 'clamp(14px, 3vw, 18px)',
    color: '#24292e',
    margin: '18px 0 4px',
    fontWeight: 500,
  },
  guideSub: { fontSize: 'clamp(11px, 2.3vw, 14px)', color: '#6c757d', margin: '4px 0 0' },
  warn: { fontSize: 'clamp(12px, 2.5vw, 15px)', color: '#cf222e', margin: '12px 0 0' },

  person: {
    padding: 'clamp(14px, 3vw, 20px)',
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    marginBottom: 'clamp(16px, 3.5vw, 24px)',
  },
  personNo: { fontSize: 13, color: '#6c757d', minHeight: '1em' },
  personName: {
    fontSize: 'clamp(26px, 6.5vw, 44px)',
    fontWeight: 600,
    color: '#24292e',
    marginTop: 4,
  },

  strip: {
    display: 'grid',
    gap: 'clamp(10px, 2.5vw, 20px)',
    padding: 'clamp(14px, 3vw, 20px)',
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    marginBottom: 'clamp(16px, 3.5vw, 24px)',
  },
  stripLabel: { fontSize: 'clamp(11px, 2.3vw, 13px)', color: '#6c757d', marginBottom: 6 },
  stripValue: { fontSize: 'clamp(17px, 3.6vw, 24px)', fontWeight: 500 },
  stripNote: {
    gridColumn: '1 / -1',
    fontSize: 'clamp(11px, 2.2vw, 12px)',
    color: '#57606a',
  },

  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'clamp(10px, 2.5vw, 16px)',
    justifyContent: 'center',
  },
  actionBtn: {
    // 立ったまま、急いで押される。指で確実に当たる大きさを確保する
    minWidth: 'clamp(130px, 30vw, 190px)',
    padding: 'clamp(16px, 3.5vw, 24px) clamp(18px, 4vw, 32px)',
    fontSize: 'clamp(18px, 4vw, 26px)',
    fontWeight: 600,
    color: 'white',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    transition: 'all 0.15s ease',
  },
  cameraBtn: {
    marginTop: 12,
    backgroundColor: 'transparent',
    color: '#0969da',
    border: '1px solid #d0d7de',
    borderRadius: 6,
    padding: '8px 18px',
    fontSize: 'clamp(12px, 2.5vw, 14px)',
    cursor: 'pointer',
  },
  cancelBtn: {
    marginTop: 'clamp(14px, 3vw, 20px)',
    backgroundColor: 'transparent',
    color: '#6c757d',
    border: '1px solid #d0d7de',
    borderRadius: 6,
    padding: '10px 22px',
    fontSize: 'clamp(12px, 2.5vw, 14px)',
    cursor: 'pointer',
  },

  result: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 },
  mark: {
    width: 'clamp(56px, 12vw, 84px)',
    height: 'clamp(56px, 12vw, 84px)',
    borderRadius: '50%',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 'clamp(32px, 7vw, 48px)',
    fontWeight: 700,
  },
  resultText: { fontSize: 'clamp(20px, 4.5vw, 30px)', fontWeight: 600 },
  errorText: {
    fontSize: 'clamp(15px, 3.2vw, 20px)',
    color: '#cf222e',
    margin: 0,
    maxWidth: '85%',
  },
};

export default KioskPage;
